import { useCallback, useEffect, useState } from "react";
import { DateRange } from "react-day-picker";

import {
  formatDateTimeForDisplay,
  isCompletedDateRange,
  parseDateRangeValue,
  serializeDateRangeValue,
  serializeDateTimeValue,
} from "../value-utils";
import { resolveFilterBuilderInputMode } from "../../config/filter-value-type-behavior";
import { getOperatorsForFilter, shouldSuppressValueDropdown } from "../../config/schema-utils";
import type { StructuredSearchStore } from "../../stores";
import { FilterBuilderDateInputType, FilterBuilderInputMode } from "../../types";

const getInputMode = (store: StructuredSearchStore): FilterBuilderInputMode => {
  if (store.step !== "value" || !store.inProgress.type || !store.inProgress.operator) {
    return FilterBuilderInputMode.Text;
  }

  const opDef = getOperatorsForFilter(store.schema, store.inProgress.field).find(
    op => op.operator === store.inProgress.operator,
  );

  return resolveFilterBuilderInputMode(
    store.inProgress.type,
    store.inProgress.operator,
    opDef?.valueInput === "none",
  );
};

const getDateInputType = (inputMode: FilterBuilderInputMode): FilterBuilderDateInputType => {
  switch (inputMode) {
    case FilterBuilderInputMode.Date:
      return FilterBuilderDateInputType.Date;
    case FilterBuilderInputMode.DateRange:
      return FilterBuilderDateInputType.DateRange;
    default:
      return FilterBuilderDateInputType.None;
  }
};

const clearDateValue = (store: StructuredSearchStore) => {
  store.setInputText("");
  store.setValue("");
};

const setSingleDateValue = (store: StructuredSearchStore, nextDate: DateRange): void => {
  if (!nextDate.from) {
    clearDateValue(store);
    return;
  }

  const serializedDate = serializeDateTimeValue(nextDate.from);
  const displayDate = formatDateTimeForDisplay(nextDate.from);
  store.setInputText(displayDate);
  store.setValue(serializedDate, displayDate);
};

const setDateRangeValue = (store: StructuredSearchStore, nextDate: DateRange): void => {
  if (!isCompletedDateRange(nextDate)) {
    clearDateValue(store);
    return;
  }

  const serializedRange = serializeDateRangeValue(nextDate);
  const displayRange = `${formatDateTimeForDisplay(nextDate.from!)} - ${formatDateTimeForDisplay(nextDate.to!)}`;
  store.setInputText(displayRange);
  store.setValue(serializedRange, displayRange);
};

export const useFilterBuilderValueState = (store: StructuredSearchStore) => {
  const [datePickerRange, setDatePickerRange] = useState<DateRange | undefined>(undefined);

  const inputMode = getInputMode(store);
  const dateInputType = getDateInputType(inputMode);
  const isDateInput = store.step === "value" && dateInputType === FilterBuilderDateInputType.Date;
  const isDateRangeInput =
    store.step === "value" && dateInputType === FilterBuilderDateInputType.DateRange;
  const operatorNeedsValue = inputMode !== FilterBuilderInputMode.None;
  const shouldShowValueDropdown =
    store.step !== "value" ||
    !shouldSuppressValueDropdown(store.schema, store.inProgress.field, store.inProgress.operator);
  const canApplyDateValue =
    (dateInputType === FilterBuilderDateInputType.Date && Boolean(datePickerRange?.from)) ||
    (dateInputType === FilterBuilderDateInputType.DateRange &&
      isCompletedDateRange(datePickerRange));

  useEffect(() => {
    if (dateInputType === FilterBuilderDateInputType.None) {
      setDatePickerRange(undefined);
      return;
    }

    setDatePickerRange(parseDateRangeValue(store.inProgress.value));
  }, [dateInputType, store.editingFilterId, store.inProgress.value]);

  const handleDatePickerChange = useCallback((nextDate: DateRange | undefined) => {
    setDatePickerRange(nextDate);
  }, []);

  const handleApplyDatePicker = useCallback(() => {
    if (!datePickerRange) {
      clearDateValue(store);
      return;
    }

    if (dateInputType === FilterBuilderDateInputType.Date) {
      if (!datePickerRange.from) {
        return;
      }
      setSingleDateValue(store, datePickerRange);
      store.completeFilter();
      return;
    }

    if (dateInputType === FilterBuilderDateInputType.DateRange) {
      if (!isCompletedDateRange(datePickerRange)) {
        return;
      }
      setDateRangeValue(store, datePickerRange);
      store.completeFilter();
    }
  }, [dateInputType, datePickerRange, store]);

  const handleClearDatePicker = useCallback(() => {
    setDatePickerRange(undefined);
    clearDateValue(store);
  }, [store]);

  const hasDatePickerValue = Boolean(datePickerRange?.from);

  return {
    canApplyDateValue,
    datePickerRange,
    handleApplyDatePicker,
    handleClearDatePicker,
    handleDatePickerChange,
    hasDatePickerValue,
    inputMode,
    inputValue: store.inputText,
    isDateInput,
    isDateRangeInput,
    operatorNeedsValue,
    shouldShowValueDropdown,
  };
};
